import { computed, ref } from "vue";
import { Loader } from "@allwyn/design-system/src/loader/loader";
import { debounce } from "@allwyn/utils/debounce";
import { QuickSearchAnalytics } from "./quick-search-analytics";
import { QuickSearchApi } from "../api/quick-search-api";
import { QuickSearchTipModel } from "../models/quick-search-models";

const MAX_SEARCH_LENGTH = 32;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 200;
const ANALYTICS_DEBOUNCE_MS = 1000;
const searchText = ref("");
const searchTips = ref<QuickSearchTipModel[]>([]);
const isActive = ref(false);
const showDefaultTips = ref(false);
const isSearchResultPage = environment?.nodeAliasPath === "/vyhledavani";
// TODO: make it editable from CMS
const quickSearchResults = [
    {
        category: "Sportka",
        title: "",
        imageUrl:
            "https://static.sazka.cz/kentico-media/sazka/media/content/loterie/sportka/karty/sportka-350x280@1x.jpg?ext=.jpg",
        url: "/loterie/sportka",
        type: "lottery",
    },
    {
        category: "Eurojackpot",
        title: "",
        imageUrl:
            "https://static.sazka.cz/kentico-media/sazka/media/content/loterie/eurojackpot/karty/eurojackpot-330x280@1x.jpg?ext=.jpg",
        url: "/loterie/eurojackpot",
        type: "lottery",
    },
    {
        category: "Sazka Hry",
        title: "",
        imageUrl: "https://static.sazka.cz/kentico-media/sazka/media/content/vyhledavani/vyhl-hry.jpg?ext=.jpg",
        url: "/hry",
        type: "game",
    },
    {
        category: "Kolo \u0161t\u011Bst\u00ED",
        title: "",
        imageUrl: "https://static.sazka.cz/kentico-media/sazka/media/content/vyhledavani/vyhl-kolo-stesti.jpg?ext=.jpg",
        url: "/sazka-klub",
        type: "lottery",
    },
    {
        category: "Mobiln\u00ED aplikace",
        title: "",
        imageUrl: "https://static.sazka.cz/kentico-media/sazka/media/content/system/vyhl-aplikace.jpg?ext=.jpg",
        url: "/mobilni-aplikace",
        type: "",
    },
    {
        category: "Registrace",
        title: "",
        imageUrl: "https://static.sazka.cz/kentico-media/sazka/media/content/system/vyhl-registrace_1.jpg?ext=.jpg",
        url: "/registrace",
        type: "",
    },
];

// Track last searched for analytics
let lastSearchedText = "";
let lastResultsCount = 0;

const hasSearchText = computed(() => searchText.value.length > 0);
const canSearch = computed(() => searchText.value.length >= MIN_SEARCH_LENGTH);

const showAutocomplete = computed(() => {
    return (isActive.value || isSearchResultPage) && (hasSearchText.value || showDefaultTips.value);
});

const searchUrl = computed(() => {
    return `/vyhledavani?searchtext=${encodeURIComponent(searchText.value)}&searchmode=anyword`;
});

function limitSearchText(text: string): string {
    return text.substring(0, MAX_SEARCH_LENGTH);
}

function clearSearch() {
    searchText.value = "";
    searchTips.value = [];
    showDefaultTips.value = false;
}

function hideAutocomplete() {
    searchTips.value = [];
    showDefaultTips.value = false;
}

function activate() {
    isActive.value = true;
}

function deactivate() {
    if (!isSearchResultPage) {
        isActive.value = false;
        clearSearch();
    }
    hideAutocomplete();
}

function showDefaultResults() {
    console.log("showDefaultResults called");

    if (!searchText.value && quickSearchResults?.length) {
        console.log("Showing default quick search results");

        showDefaultTips.value = true;
        searchTips.value = quickSearchResults;
    }
}

const fetchResultsDebounced = debounce(async () => {
    if (!canSearch.value) {
        return;
    }

    try {
        const response = await QuickSearchApi.getSearchResult(searchText.value);
        lastSearchedText = searchText.value;
        lastResultsCount = response.value?.length ?? 0;

        if (response.value?.length) {
            searchTips.value = response.value;
            showDefaultTips.value = false;
        } else {
            hideAutocomplete();
        }
    } catch (error) {
        console.error("Quick search error:", error);
        hideAutocomplete();
    }
}, SEARCH_DEBOUNCE_MS);

const pushAnalyticsDebounced = debounce(() => {
    if (lastSearchedText) {
        QuickSearchAnalytics.pushSearchSuggester("main", lastSearchedText, lastResultsCount);
    }
}, ANALYTICS_DEBOUNCE_MS);

function onInput(value: string) {
    searchText.value = limitSearchText(value);
    showDefaultTips.value = false;

    if (!canSearch.value) {
        hideAutocomplete();
        return;
    }

    fetchResultsDebounced();
    pushAnalyticsDebounced();
}

function navigateToSearch() {
    if (!searchText.value) {
        return;
    }

    Loader.show();
    window.location.href = searchUrl.value;
}

function onSubmit() {
    if (isActive.value) {
        navigateToSearch();
        deactivate();
    } else {
        activate();
    }
}

function onTipClick(tip: QuickSearchTipModel) {
    const name = `${tip.category}${tip.title ? `.${tip.title}` : ""}`;
    QuickSearchAnalytics.pushSuggesterClick(QuickSearchAnalytics.getVerticalByCategory(tip.type), name);
    deactivate();
}

function adjustUrl(url: string): string {
    return url.startsWith("/") ? url.substring(1) : url;
}

function getTipUrl(tip: QuickSearchTipModel): string | null {
    if (tip.externalSystemId) {
        return null; // Game launcher handles these
    }
    return `/${adjustUrl(tip.url)}`;
}

function getTipButtonLabel(tip: QuickSearchTipModel): string | null {
    if (!tip.externalSystemId) {
        return null;
    }
    return tip.type === "game" ? "Hrát hru" : "Setřít los";
}

export function useQuickSearch() {
    return {
        // State
        searchText,
        searchTips,
        isActive,
        showDefaultTips,
        isSearchResultPage,

        // Computed
        hasSearchText,
        canSearch,
        showAutocomplete,
        searchUrl,

        // Actions
        onInput,
        onSubmit,
        onTipClick,
        activate,
        deactivate,
        clearSearch,
        hideAutocomplete,
        showDefaultResults,

        // Helpers
        getTipUrl,
        getTipButtonLabel,
    };
}
